import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  Injector,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, withPreloading, withRouterConfig } from '@angular/router';
import {
  HttpBackend,
  HttpClient,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { LocationStrategy } from '@angular/common';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { HttpCacheInterceptorModule, useHttpCacheLocalStorage } from '@ngneat/cashew';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { ToastrModule } from 'ngx-toastr';
import { environment } from '@environments/environment';
import { enableElfProdMode } from '@ngneat/elf';
import { devTools } from '@ngneat/elf-devtools';
import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';
import { AppComponent } from '@app/app.component';
import { TesterModalService } from '@app/shared/modals/tester-modal/tester-modal.service';
import { routes } from '@app/app-routing.module';
import { SharedModule } from '@shared/shared.module';
import { HelperService } from '@shared/services/helper.service';
import { CustomMultiTranslateHttpLoader } from '@app/shared/utils/locale-manager';
import { NullLocationStrategy } from '@app/shared/utils/null-location-strategy.service';
import { CachingInterceptor } from '@shared/interceptors/caching-interceptor';
import { ClockSyncInterceptor } from '@shared/interceptors/clock-sync.interceptor';
import { RetryHttpErrorsInterceptor } from '@shared/interceptors/retry-interceptor';
import { TokenInterceptor } from '@shared/interceptors/token-interceptor';
import { NativeService } from '@shared/services/native.service';
import { LanguageService } from '@app/shared/utils/language.service';
import { AnalyticsService } from '@shared/services/analytics.service';
import { RemoteConfigInitializer } from '@app/core/firebase/remote-config-initializer.service';
import { ThemingService } from '@shared/services/theming.service';
import { NativeAnalyticsService } from '@app/core/analytics/native-analytics.service';
import { WebAnalyticsService } from '@app/core/analytics/web-analytics.service';
import { ANALYTICS_CONFIG } from '@app/core/analytics/firehose/analytics.config';
import { FirehoseContextService } from '@app/core/analytics/firehose/firehose-context.service';
import { NativeRemoteConfigService } from '@app/core/firebase/native-remote-config.service';
import { WebRemoteConfigService } from '@app/core/firebase/web-remote-config.service';
import { exposeModalBlocker } from '@app/core/tools/modal-blocker-exposure';
import { SingleFlightInterceptor } from '@app/shared/interceptors/single-flight-interceptor/single-flight-interceptor';

if (environment.production) {
  enableProdMode();
  enableElfProdMode();
} else {
  devTools();
}

if (environment.name == 'PRODUCTION') {
  Sentry.init({
    dsn: 'https://691b28dc2801c95b520d2b751d6ee3a9@o392098.ingest.sentry.io/4506025146187776',
    environment: environment.name,
    release: environment.VERSION,
    tracePropagationTargets: [
      /^https:\/\/as-slotcom-challenges\.azurewebsites/,
      /^https:\/\/shopoffers-api\.slot\.com/,
      /^https:\/\/portal-api\.slot\.com/,
      /^https:\/\/jackpots\.slot\.com/,
      /^https:\/\/signalr-pro\.slot\.com/,
      /^https:\/\/api\.playspace\.com/,
    ],
    integrations: [Sentry.browserTracingIntegration(), Sentry.captureConsoleIntegration({ levels: ['error'] })],
    sampleRate: 0.01,
    beforeSend(event) {
      if (
        event.exception?.values &&
        event.exception.values[0].mechanism.type === 'console' &&
        event.exception.values[0].stacktrace.frames &&
        event.exception.values[0].stacktrace.frames[0].filename.indexOf('cdn.onesignal.com') >= 0
      ) {
        return null;
      }

      return event;
    },
  });
}
function httpLoaderFactory(handler: HttpBackend, helper: HelperService) {
  const http = new HttpClient(handler);
  const commonPrefix = helper.getAdminTranslationContent('/live/slotcom/portal_');
  const legalPrefix = helper.getAdminTranslationContent('/live/slotcom/legal_');

  return new CustomMultiTranslateHttpLoader(http, [
    { prefix: commonPrefix, suffix: '.json' },
    { prefix: legalPrefix, suffix: '.json' },
  ]);
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZonelessChangeDetection(),
    provideAnimations(),
    provideRouter(routes, withPreloading(PreloadAllModules), withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    importProvidersFrom(
      HttpCacheInterceptorModule.forRoot(),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
          deps: [HttpBackend, HelperService],
        },
      }),
      ToastrModule.forRoot(),
      SharedModule
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SingleFlightInterceptor,
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(() => inject(NativeService).waitForNativeInitialization()),
    NativeAnalyticsService,
    WebAnalyticsService,
    NativeRemoteConfigService,
    WebRemoteConfigService,
    {
      provide: ANALYTICS_CONFIG,
      useValue: {
        ...environment.firehoseAnalytics,
        appVersion: environment.VERSION,
        debug: environment.firehoseAnalytics.debug,
      },
    },
    provideAppInitializer(() => inject(LanguageService).initUserLanguage()),
    provideAppInitializer(() => inject(AnalyticsService).initialize()),
    provideAppInitializer(() => inject(RemoteConfigInitializer).initialize()),
    provideAppInitializer(() => inject(ThemingService).load()),
    provideAppInitializer(() => {
      const injector = inject(Injector);
      exposeModalBlocker(injector);
    }),
    ...(environment.production
      ? []
      : [
          provideAppInitializer(() => {
            inject(TesterModalService);
          }),
        ]),
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(async () => {
      const contextService = inject(FirehoseContextService);

      await contextService.ready();
    }),
    { provide: HTTP_INTERCEPTORS, useClass: CachingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ClockSyncInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RetryHttpErrorsInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ showDialog: false }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { closeOnNavigation: false, hasBackdrop: true },
    },
    {
      provide: LocationStrategy,
      useClass: NullLocationStrategy,
    },
    useHttpCacheLocalStorage,
  ],
}).catch((err) => console.error(err));
